import styled from "styled-components";
import { useMemo, useState } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";

import { SectionHeading, SrOnly } from "@/components/Layout/Parts";
import { ResponsiveTable, Table, TableContent, Th } from "@/components/Table";
import SearchesTableRow from "./SearchesTableRow";
import { SerpSearchResource, PaginatedResponse } from "@/types/resources";
import { api } from "@/utils/api";
import InfiniteLoader from "@/components/InfiniteLoader";
import SearchBar from "@/components/SearchBar";
import useDebounce from "@/hooks/useDebounce";

const StyledTable = styled(Table)`
  th,
  td {
    padding: 16px 12px;
    font-size: 11px;
    vertical-align: middle;
    width: 25%;
    font-weight: 700;
    &:first-child {
      padding-left: 24px;
    }
    &:nth-child(4) {
      width: calc(25% - 56px);
    }
    &:last-child {
      padding-right: 24px;
      width: 56px;
      position: relative;
    }
  }
`;

const StyledSearchBar = styled(SearchBar)`
  margin-bottom: 12px;
`;

const SearchesTable = () => {
  const [search, setSearch] = useState<string>("");

  const debouncedSearch = useDebounce(search, 500);

  const fetchSearches = ({ pageParam = "/v1/amazon-searches" }) => {
    return api.get<never, PaginatedResponse<SerpSearchResource>>(pageParam, {
      params: {
        ...(!!debouncedSearch && {
          "filter[search]": debouncedSearch,
        }),
      },
    });
  };

  const {
    data,
    isError,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useInfiniteQuery<PaginatedResponse<SerpSearchResource>>(
    ["amazon_searches_list", { debouncedSearch }],
    fetchSearches,
    {
      getNextPageParam: (lastPage) => {
        return lastPage.links.next;
      },
    }
  );

  const searches = useMemo(
    () =>
      Object.keys(data?.pages || {}).reduce<SerpSearchResource[]>(
        (acc, currKey: any) => {
          return [...acc, ...data!.pages[currKey].data];
        },
        []
      ),
    [data]
  );

  return (
    <>
      <SectionHeading>Recent Searches</SectionHeading>
      <StyledSearchBar
        setValue={setSearch}
        value={search}
        placeholder="Search by Search Query"
      />
      <ResponsiveTable>
        <StyledTable $fixed>
          <thead>
            <tr>
              <Th>Search Query</Th>
              <Th>Date Created</Th>
              <Th>Creator</Th>
              <Th>Results Count</Th>
              <Th>
                <SrOnly>Actions</SrOnly>
              </Th>
            </tr>
          </thead>
          <tbody>
            <TableContent
              isLoading={isLoading}
              colSpan={5}
              isError={isError}
              hasData={!!searches.length}
            >
              {searches?.map((search) => (
                <SearchesTableRow key={search.id} search={search} />
              ))}
            </TableContent>
          </tbody>
        </StyledTable>
      </ResponsiveTable>
      <InfiniteLoader
        hasNextPage={hasNextPage}
        fetchNextPage={fetchNextPage}
        isFetchingNextPage={isFetchingNextPage}
      />
    </>
  );
};

export default SearchesTable;
